import type { ICategoryRepository, PaginatedResponse, QueryParams } from '@yawi/domain';
import { buildQueryString, DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '@yawi/domain';
import type { Category, CategoryKind, CreateCategoryDTO, UpdateCategoryDTO } from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

interface ApiCategoryResponse {
  id: string;
  name: string;
  kind: CategoryKind;
  color: string | null;
  icon: string | null;
  isSystem: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const mapApiCategoryToCategory = (api: ApiCategoryResponse): Category => ({
  id: api.id,
  name: api.name,
  kind: api.kind,
  color: api.color,
  icon: api.icon,
  isSystem: api.isSystem,
  isActive: api.isActive,
  createdAt: api.createdAt,
  updatedAt: api.updatedAt,
});

export class ApiCategoryRepository implements ICategoryRepository {
  async getActive(kind: CategoryKind = 'EXPENSE'): Promise<Category[]> {
    const client = getApiClient();
    const response = await client.get<ApiCategoryResponse[]>(`v1/categories/active?kind=${kind}`);
    return response.map(mapApiCategoryToCategory);
  }

  async getCategories(params?: QueryParams): Promise<PaginatedResponse<Category>> {
    const client = getApiClient();
    const queryString = buildQueryString({
      page: params?.page ?? DEFAULT_PAGE,
      limit: params?.limit ?? DEFAULT_PAGE_SIZE,
      search: params?.search,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      ...params,
    });
    const response = await client.get<PaginatedResponse<ApiCategoryResponse>>(
      `v1/categories?${queryString}`,
    );
    return { ...response, data: response.data.map(mapApiCategoryToCategory) };
  }

  async getCategoryById(id: string): Promise<Category | null> {
    const client = getApiClient();
    try {
      const category = await client.get<ApiCategoryResponse>(`v1/categories/${id}`);
      return mapApiCategoryToCategory(category);
    } catch {
      return null;
    }
  }

  async createCategory(data: CreateCategoryDTO): Promise<Category> {
    const client = getApiClient();
    const category = await client.post<ApiCategoryResponse>('v1/categories', data);
    return mapApiCategoryToCategory(category);
  }

  async updateCategory(id: string, data: UpdateCategoryDTO): Promise<Category> {
    const client = getApiClient();
    const category = await client.put<ApiCategoryResponse>(`v1/categories/${id}`, data);
    return mapApiCategoryToCategory(category);
  }

  async deleteCategory(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`v1/categories/${id}`);
  }
}
